import React from "react";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import { createStore, applyMiddleware, compose } from "redux";
import { Provider } from "react-redux";
import { routerMiddleware } from "connected-react-router";
import { createBrowserHistory } from "history";
import lmsReducer from "./reducers/lmsReducer";
import Routes from "./routes";
import { getSession } from "./utils/getSession";
import applyGlobalStyles from "./components/library/applyGlobalStyles";
import { NODE_ENV, BUGSNAG_API_KEY } from "@shared/env";
import UpdateAvailableToast from "@shared/components/UpdateAvailableToast";
import bugsnagShouldReport from "./utils/bugsnagShouldReport";
import { initPostHog } from "./utils/posthog";

const applyDevTools = devTools => {
  return devTools && NODE_ENV === "development" ? devTools() : f => f;
};

// Bugsnag requires a non-empty apiKey at init time and throws synchronously
// otherwise — that uncaught exception crashes the bundle before React mounts,
// which is exactly what was happening in CI (BUGSNAG_API_KEY env unset).
// Reports only fire in production anyway (enabledReleaseStages), so skipping
// init in environments without a key is harmless.
if (BUGSNAG_API_KEY) {
  Bugsnag.start({
    apiKey: BUGSNAG_API_KEY,
    plugins: [new BugsnagPluginReact()],
    releaseStage: NODE_ENV,
    enabledReleaseStages: ["production"],
    onError: bugsnagShouldReport,
  });
}

const ErrorBoundary = BUGSNAG_API_KEY
  ? Bugsnag.getPlugin("react").createErrorBoundary(React)
  : ({ children }) => <>{children}</>;

// Boots product analytics before React mounts so the first page view is
// captured. No-op in local development / without a key (see initPostHog).
initPostHog();

const currentUserId = getSession()?.currentUserId;
const initialState = { currentUserId };
const browserHistory = createBrowserHistory();
const store = createStore(
  lmsReducer(browserHistory),
  initialState,
  compose(
    applyMiddleware(routerMiddleware(browserHistory)),
    applyDevTools(window.__REDUX_DEVTOOLS_EXTENSION__)
  )
);

applyGlobalStyles();

export default function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <Routes history={browserHistory} />
        <UpdateAvailableToast />
      </Provider>
    </ErrorBoundary>
  );
}
